import { defineStore } from "pinia";
import qbankService from "@/services/qbank";

export const useQBankStore = defineStore("qbank", {
  state: () => ({
    // Statistics data
    statistics: null,
    statisticsLoading: false,
    statisticsError: null,

    // Categories/Lessons data
    categories: [],
    categoriesLoading: false,
    categoriesError: null,

    // Predefined QBanks
    predefinedTypes: [],
    predefinedTypesLoading: false,
    sources: [],
    sourcesLoading: false,
    predefinedQBanks: [],
    predefinedQBanksLoading: false,
    predefinedQBanksPagination: {
      currentPage: 1,
      lastPage: 1,
      total: 0,
    },

    // Create test state
    createTestLoading: false,
    createTestError: null,

    // Validation state
    validatedCount: null,
    validationLoading: false,

    // Current subscription plan ID
    currentSubscriptionPlanId: null,
  }),

  getters: {
    totalQuestions: (state) =>
      state.statistics?.total_questions ??
      state.statistics?.data?.total_questions ??
      0,

    questionModes: (state) => state.statistics?.question_modes || [],

    unusedMode: (state) => {
      const modes = state.statistics?.question_modes || [];
      return (
        modes.find((m) => m.key === "unused") || {
          key: "unused",
          name: "Unused",
          count: 0,
        }
      );
    },

    usedModes: (state) => {
      const modes = state.statistics?.question_modes || [];
      return modes.filter((m) => m.key !== "unused");
    },

    totalUsedCount: (state) => {
      const modes = state.statistics?.question_modes || [];
      return modes
        .filter((m) => m.key !== "unused")
        .reduce((sum, m) => sum + m.count, 0);
    },

    difficultyLevels: (state) => state.statistics?.difficulty_levels || [],

    statisticsSources: (state) => state.statistics?.sources || [],

    questionTypes: (state) => state.statistics?.question_types || [],

    // Flatten all lessons from categories
    allLessons: (state) => {
      return state.categories.flatMap((cat) => cat.lessons || []);
    },
  },

  actions: {
    setSubscriptionPlanId(id) {
      this.currentSubscriptionPlanId = id;
    },

    async loadStatistics(subscriptionPlanId) {
      const planId = subscriptionPlanId || this.currentSubscriptionPlanId;
      if (!planId) return;

      this.statisticsLoading = true;
      this.statisticsError = null;
      try {
        const response = await qbankService.fetchStatistics(planId);
        this.statistics = response.data || response;
      } catch (error) {
        this.statisticsError = error.message;
        console.error("Failed to load statistics:", error);
      } finally {
        this.statisticsLoading = false;
      }
    },

    async loadCategories(subscriptionPlanId, options = {}) {
      const planId = subscriptionPlanId || this.currentSubscriptionPlanId;
      if (!planId) return;

      this.categoriesLoading = true;
      this.categoriesError = null;
      try {
        const response = await qbankService.fetchCategories(planId, options);
        this.categories = response.data || response || [];
      } catch (error) {
        this.categoriesError = error.message;
        console.error("Failed to load categories:", error);
      } finally {
        this.categoriesLoading = false;
      }
    },

    async validateQuestionCount(filters, subscriptionPlanId = null) {
      const planId = subscriptionPlanId || this.currentSubscriptionPlanId;
      if (!planId) return null;

      this.validationLoading = true;
      try {
        // Service now returns the count directly
        const count = await qbankService.validateCount(planId, filters);
        if (typeof count === "number") {
          this.validatedCount = count;
          return count;
        }
        return null;
      } catch (error) {
        console.error("Failed to validate count:", error);
        return null;
      } finally {
        this.validationLoading = false;
      }
    },

    async createCustomTest(testData) {
      const planId = this.currentSubscriptionPlanId;
      if (!planId) throw new Error("No subscription plan selected");

      this.createTestLoading = true;
      this.createTestError = null;
      try {
        const response = await qbankService.createTest(planId, testData);
        return response.data || response;
      } catch (error) {
        this.createTestError = error.message;
        throw error;
      } finally {
        this.createTestLoading = false;
      }
    },

    async loadPredefinedTypes(subscriptionPlanId) {
      const planId = subscriptionPlanId || this.currentSubscriptionPlanId;
      if (!planId) return;

      this.predefinedTypesLoading = true;
      try {
        const response = await qbankService.fetchPredefinedTypes(planId);
        this.predefinedTypes = response.data || response || [];
      } catch (error) {
        console.error("Failed to load predefined types:", error);
      } finally {
        this.predefinedTypesLoading = false;
      }
    },

    async loadSources() {
      this.sourcesLoading = true;
      try {
        const response = await qbankService.fetchSources();
        this.sources = response.data || response || [];
      } catch (error) {
        console.error("Failed to load sources:", error);
      } finally {
        this.sourcesLoading = false;
      }
    },

    async loadPredefinedQBanks(filters = {}, append = false) {
      const planId = this.currentSubscriptionPlanId;
      if (!planId) return;

      this.predefinedQBanksLoading = true;
      try {
        const response = await qbankService.fetchPredefinedQBanks(
          planId,
          filters
        );
        const data = response.data || [];

        // Add is_used property based on cloned_bank
        const processedData = data.map((qbank) => ({
          ...qbank,
          is_used:
            qbank.cloned_bank !== null && qbank.cloned_bank !== undefined,
        }));

        if (append) {
          this.predefinedQBanks = [...this.predefinedQBanks, ...processedData];
        } else {
          this.predefinedQBanks = processedData;
        }

        // Update pagination info
        if (response.meta) {
          this.predefinedQBanksPagination = {
            currentPage: response.meta.current_page || 1,
            lastPage: response.meta.last_page || 1,
            total: response.meta.total || 0,
          };
        }
      } catch (error) {
        console.error("Failed to load predefined QBanks:", error);
      } finally {
        this.predefinedQBanksLoading = false;
      }
    },

    async cloneQBank(predefinedQBankId) {
      const planId = this.currentSubscriptionPlanId;
      if (!planId) throw new Error("No subscription plan selected");

      try {
        const response = await qbankService.clonePredefinedQBank(
          planId,
          predefinedQBankId
        );
        return response;
      } catch (error) {
        throw error;
      }
    },

    resetState() {
      this.statistics = null;
      this.categories = [];
      this.predefinedTypes = [];
      this.sources = [];
      this.predefinedQBanks = [];
      this.validatedCount = null;
      this.createTestError = null;
    },
  },
});
